import React, { useState, useEffect } from "react";
import styles from "./PortalNavbar.module.css";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import supabase from "../supabase";

const PortalNavbar = ({
  collapsed,
  toggleCollapsed,
  profile,
  organization,
  onGuardNavigate,
}) => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const handleNavClick = (e, to, options) => {
    e.preventDefault();
    (async () => {
      await onGuardNavigate?.();
      navigate(to, options);
    })();
  };
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadRequestsCount, setUnreadRequestsCount] = useState(0);

  // New state to track which link is being hovered
  const [hoveredLink, setHoveredLink] = useState(null);

  const orgId = organization?.id || profile?.company_id || companyId;

  useEffect(() => {
    if (!profile?.id || !orgId) return;

    const fetchUnread = async () => {
      try {
        const {
          count,
          error,
          status: httpStatus,
        } = await supabase
          .from("messages")
          .select("id", { count: "exact" }) // we only need the count
          .eq("org_id", orgId) // ✅ Lock to Tenant
          .eq("to_user", profile.id) // recipient is this user
          .eq("status", "unread"); // unread only

        if (error) {
          console.error("[navbar] unread count error:", { error, httpStatus });
          setUnreadCount(0);
          return;
        }
        setUnreadCount(count ?? 0);
      } catch (e) {
        console.error("[navbar] unread count exception:", e);
        setUnreadCount(0);
      }
    };

    fetchUnread();

    // live updates (new messages or status changes)
    const channel = supabase
      .channel("messages-unread-badge")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
          filter: `to_user=eq.${profile.id}`,
        },
        () => fetchUnread()
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [profile?.id, orgId]);

  useEffect(() => {
    if (organization?.id !== "821b771e-d2ea-4b98-8570-c59e4e14bed5") return;

    const fetchUnreadRequests = async () => {
      try {
        const { count, error } = await supabase
          .from("Requests")
          .select("id", { count: "exact" })
          .eq("opened", false);

        if (error) {
          console.error("[navbar] unread requests count error:", error);
          setUnreadRequestsCount(0);
          return;
        }
        setUnreadRequestsCount(count ?? 0);
      } catch (e) {
        console.error("[navbar] unread requests count exception:", e);
        setUnreadRequestsCount(0);
      }
    };

    fetchUnreadRequests();

    // live updates (new requests received or marked opened)
    const channel = supabase
      .channel("requests-unread-badge")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "Requests" },
        () => fetchUnreadRequests()
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [organization?.id]);

  // Styles for the floating tooltip
  const tooltipStyle = {
    position: "absolute",
    left: "50%", // Center horizontally relative to parent
    top: "80%", // Moved up from 100% to 80%
    transform: "translateX(-50%)", // Center the tooltip itself
    marginTop: "0px", // Removed extra spacing
    backgroundColor: "#1f2937", // Dark grey/black
    color: "white",
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "600",
    whiteSpace: "nowrap",
    zIndex: 9999999, // High z-index
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    pointerEvents: "none", // Prevent flickering if mouse touches it
  };

  // Tooltip for the sidebar collapse/expand toggle (centered under the row so it
  // doesn't get clipped by the sidebar's overflow:hidden when collapsed/narrow)
  const sidebarToggleTooltipStyle = {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#1f2937",
    color: "white",
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: "600",
    whiteSpace: "nowrap",
    zIndex: 9999999,
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    pointerEvents: "none",
  };

  // dashboard.png and upload.png are bold glyphs that fill ~75% of their
  // 512x512 canvas. The other five nav icons are thin-line glyphs that only
  // fill ~20% of their own (much smaller, ~102px) canvas — measured via a
  // PNG ink-bbox analysis. Because CSS maxWidth scales the whole canvas
  // (including baked-in transparent padding), these two groups need
  // different maxWidth values to render the same *visible* icon size —
  // giving them the same box size makes the thin-line group look tiny.
  // Do not unify these into one value without re-measuring. If either
  // value changes, keep `.navCollapsed .navLink`'s min-height in
  // PortalNavbar.module.css >= the tallest rendered icon + ~20px padding.
  const boldIconStyle = {
    // dashboard.png, upload.png
    width: "100%",
    height: "auto",
    maxWidth: "24px",
    objectFit: "contain",
    display: "block",
    margin: "0 auto",
  };

  const thinLineIconStyle = {
    // Create_Circle, Articles, Projects, Messages, Team_Members
    ...boldIconStyle,
    maxWidth: "85px",
  };

  return (
    <div className={collapsed ? styles.navCollapsed : styles.navExpanded}>
      <h5
        style={{
          color: "white",
          padding: collapsed ? "40px 10px 10px 10px" : "40px 20px 10px 20px",
          whiteSpace: "nowrap",
        }}
      >
        {collapsed ? (
          <img
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "240px",
              objectFit: "contain",
              display: "block",
              maxHeight: "55px",
              margin: "0 auto",
            }}
            src="https://images.squarespace-cdn.com/content/5fa369420e4abc36602d032b/1607233514767-IN6T34ZSJG7KQOPFR71V/Underground+Logo+with+text+black+200px.png?content-type=image%2Fpng"
            alt="Logo"
          />
        ) : (
          <img
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
              maxHeight: "45px",
            }}
            src="https://images.squarespace-cdn.com/content/5fa369420e4abc36602d032b/f4b178b8-7dd2-4de1-b05b-26f3f1c1cf8e/UG_Long_New_Logo_grey.png?content-type=image%2Fpng"
            alt="Logo"
          />
        )}
      </h5>
      <hr style={{ color: "white" }} />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "0 16px 8px",
          position: "relative",
        }}
        onMouseEnter={() => setHoveredLink("sidebar-toggle")}
        onMouseLeave={() => setHoveredLink(null)}
      >
        <button
          type="button"
          onClick={toggleCollapsed}
          className={styles.toggleIconButton}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <line x1="9" y1="4" x2="9" y2="20" />
          </svg>
        </button>
        {hoveredLink === "sidebar-toggle" && (
          <div style={sidebarToggleTooltipStyle}>
            {collapsed ? "Expand sidebar" : "Collapse sidebar"}
          </div>
        )}
      </div>
      <nav>
        <ul>
          <li
            style={{ position: "relative" }}
            onMouseEnter={() => setHoveredLink("dashboard")}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <NavLink
              to={`/organization-portal/${companyId}`}
              end
              onClick={(e) => handleNavClick(e, `/organization-portal/${companyId}`)}
              className={({ isActive }) =>
                isActive
                  ? `${styles.navLink} ${styles.activeLink}`
                  : styles.navLink
              }
            >
              {collapsed ? (
                <img
                  style={boldIconStyle}
                  src="/dashboard.png"
                  alt="Dashboard"
                />
              ) : (
                "Dashboard"
              )}
            </NavLink>
            {collapsed && hoveredLink === "dashboard" && (
              <div style={tooltipStyle}>Dashboard</div>
            )}
          </li>

          {/* ✅ CONDITIONAL RENDER: Only show for non-AI organizations */}
          {!organization?.ai_enabled && (
            <li
              style={{ position: "relative" }}
              onMouseEnter={() => setHoveredLink("requests")}
              onMouseLeave={() => setHoveredLink(null)}
            >
              <NavLink
                to={`/organization-portal/${companyId}/request-center`}
                onClick={(e) =>
                  handleNavClick(e, `/organization-portal/${companyId}/request-center`)
                }
                className={({ isActive }) =>
                  isActive
                    ? `${styles.navLink} ${styles.activeLink}`
                    : styles.navLink
                }
              >
                {collapsed ? (
                  <img
                    style={boldIconStyle}
                    src="/upload.png"
                    alt="Requests"
                  />
                ) : (
                  "Requests"
                )}
              </NavLink>
              {collapsed && hoveredLink === "requests" && (
                <div style={tooltipStyle}>Requests</div>
              )}
            </li>
          )}

          <li
            style={{ position: "relative" }}
            onMouseEnter={() => setHoveredLink("generate")}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <NavLink
              to={`/organization-portal/${companyId}/content-generation`}
              onClick={(e) =>
                handleNavClick(e, `/organization-portal/${companyId}/content-generation`)
              }
              className={({ isActive }) =>
                isActive
                  ? `${styles.navLink} ${styles.activeLink}`
                  : styles.navLink
              }
            >
              {collapsed ? (
                <img
                  style={thinLineIconStyle}
                  src="/Create_Circle.png"
                  alt={organization?.ai_enabled ? "Generate Content" : "Editor"} // ✅ CONDITIONAL ALT
                />
              ) : /* ✅ CONDITIONAL RENDER FOR TEXT */
              organization?.ai_enabled ? (
                "Generate"
              ) : (
                "Editor"
              )}
            </NavLink>
            {collapsed && hoveredLink === "generate" && (
              <div style={tooltipStyle}>
                {/* ✅ CONDITIONAL RENDER FOR TOOLTIP */}
                {organization?.ai_enabled ? "Generate" : "Editor"}
              </div>
            )}
          </li>

          <li
            style={{ position: "relative" }}
            onMouseEnter={() => setHoveredLink("articles")}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <NavLink
              to={`/organization-portal/${companyId}/my-content`}
              state={{ profile }}
              onClick={(e) =>
                handleNavClick(e, `/organization-portal/${companyId}/my-content`, {
                  state: { profile },
                })
              }
              className={({ isActive }) =>
                isActive
                  ? `${styles.navLink} ${styles.activeLink}`
                  : styles.navLink
              }
            >
              {collapsed ? (
                <img
                  style={thinLineIconStyle}
                  src="/Articles.png"
                  alt="Articles"
                />
              ) : (
                "Articles"
              )}
            </NavLink>
            {collapsed && hoveredLink === "articles" && (
              <div style={tooltipStyle}>Articles</div>
            )}
          </li>

          <li
            style={{ position: "relative" }}
            onMouseEnter={() => setHoveredLink("projects")}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <NavLink
              to={`/organization-portal/${companyId}/projects`}
              onClick={(e) =>
                handleNavClick(e, `/organization-portal/${companyId}/projects`)
              }
              className={({ isActive }) =>
                isActive
                  ? `${styles.navLink} ${styles.activeLink}`
                  : styles.navLink
              }
            >
              {collapsed ? (
                <img
                  style={thinLineIconStyle}
                  src="/Projects.png"
                  alt="Projects"
                />
              ) : (
                "Projects"
              )}
            </NavLink>
            {collapsed && hoveredLink === "projects" && (
              <div style={tooltipStyle}>Projects</div>
            )}
          </li>

          {/* CONDITIONAL RENDER: Only show for master organization */}
          {organization?.id === "821b771e-d2ea-4b98-8570-c59e4e14bed5" && (
            <li
              style={{ position: "relative" }}
              onMouseEnter={() => setHoveredLink("admin-requests")}
              onMouseLeave={() => setHoveredLink(null)}
            >
              <NavLink
                to={`/organization-portal/${companyId}/requests`}
                onClick={(e) =>
                  handleNavClick(e, `/organization-portal/${companyId}/requests`)
                }
                className={({ isActive }) =>
                  isActive
                    ? `${styles.navLink} ${styles.activeLink}`
                    : styles.navLink
                }
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {collapsed ? (
                    <img
                      style={thinLineIconStyle}
                      src="/Create_Circle.png" // You can replace this icon later
                      alt="Requests"
                    />
                  ) : (
                    "Requests"
                  )}
                  {unreadRequestsCount > 0 && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 18,
                        height: 18,
                        padding: "0 6px",
                        borderRadius: 9,
                        background: "red",
                        color: "white",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {unreadRequestsCount}
                    </span>
                  )}
                </span>
              </NavLink>
              {collapsed && hoveredLink === "admin-requests" && (
                <div style={tooltipStyle}>Requests</div>
              )}
            </li>
          )}

          <li
            style={{ position: "relative" }}
            onMouseEnter={() => setHoveredLink("messages")}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <NavLink
              to={`/organization-portal/${companyId}/messages`}
              onClick={(e) =>
                handleNavClick(e, `/organization-portal/${companyId}/messages`)
              }
              className={({ isActive }) =>
                isActive
                  ? `${styles.navLink} ${styles.activeLink}`
                  : styles.navLink
              }
            >
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                {collapsed ? (
                  <img
                    style={thinLineIconStyle}
                    src="/Messages.png"
                    alt="Messages"
                  />
                ) : (
                  "Messages"
                )}
                {unreadCount > 0 && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 18,
                      height: 18,
                      padding: "0 6px",
                      borderRadius: 9,
                      background: "red",
                      color: "white",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {unreadCount}
                  </span>
                )}
              </span>
            </NavLink>
            {collapsed && hoveredLink === "messages" && (
              <div style={tooltipStyle}>Messages</div>
            )}
          </li>

          <li
            style={{ position: "relative" }}
            onMouseEnter={() => setHoveredLink("members")}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <NavLink
              to={`/organization-portal/${companyId}/members`}
              onClick={(e) =>
                handleNavClick(e, `/organization-portal/${companyId}/members`)
              }
              className={({ isActive }) =>
                isActive
                  ? `${styles.navLink} ${styles.activeLink}`
                  : styles.navLink
              }
            >
              {collapsed ? (
                <img
                  style={thinLineIconStyle}
                  src="/Team_Members.png"
                  alt="Members"
                />
              ) : (
                "Members"
              )}
            </NavLink>
            {collapsed && hoveredLink === "members" && (
              <div style={tooltipStyle}>Members</div>
            )}
          </li>
        </ul>
        <div></div>
      </nav>
    </div>
  );
};

export default PortalNavbar;
